import { useCallback, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type {
  FeatureFlagClientConfigurationMap,
  FeatureFlagTableRow,
} from '../../../types/features/featureFlags';
import featureFlagAdminService from '../services/featureFlagAdminService';
import { getSubmitErrorMessage } from '../utils/featureFlagsPageControlUtils';

type UseFeatureFlagAssignedIdsLoaderParams = {
  setClientConfigurations: Dispatch<SetStateAction<FeatureFlagClientConfigurationMap>>;
  setFeatureFormError: Dispatch<SetStateAction<string>>;
  setStoreAssignmentsChanged: Dispatch<SetStateAction<boolean>>;
  setUserAssignmentsChanged: Dispatch<SetStateAction<boolean>>;
};

export const useFeatureFlagAssignedIdsLoader = ({
  setClientConfigurations,
  setFeatureFormError,
  setStoreAssignmentsChanged,
  setUserAssignmentsChanged,
}: UseFeatureFlagAssignedIdsLoaderParams) => {
  const requestSequenceRef = useRef(0);
  const [assignedIdsError, setAssignedIdsError] = useState('');
  const [assignedIdsLoading, setAssignedIdsLoading] = useState(false);

  const resetAssignedIdsState = useCallback(() => {
    requestSequenceRef.current += 1;
    setAssignedIdsError('');
    setAssignedIdsLoading(false);
  }, []);

  const handleLoadAssignedStoreIds = useCallback(async (row: FeatureFlagTableRow) => {
    const requestSequence = requestSequenceRef.current + 1;
    requestSequenceRef.current = requestSequence;
    setAssignedIdsError('');
    setAssignedIdsLoading(true);

    try {
      const storeIds = await featureFlagAdminService.getFeatureFlagStoreIds(row.id);

      if (requestSequenceRef.current === requestSequence) {
        setClientConfigurations((currentConfigurations) => ({
          ...currentConfigurations,
          [row.client]: {
            ...currentConfigurations[row.client],
            storeIds,
          },
        }));
        setStoreAssignmentsChanged(false);
      }
    } catch (error) {
      if (requestSequenceRef.current === requestSequence) {
        const message = getSubmitErrorMessage(error, 'Failed to load assigned stores.');
        setAssignedIdsError(message);
        setFeatureFormError(message);
      }
    } finally {
      if (requestSequenceRef.current === requestSequence) {
        setAssignedIdsLoading(false);
      }
    }
  }, [setClientConfigurations, setFeatureFormError, setStoreAssignmentsChanged]);

  const handleLoadAssignedUserIds = useCallback(async (row: FeatureFlagTableRow) => {
    const requestSequence = requestSequenceRef.current + 1;
    requestSequenceRef.current = requestSequence;
    setAssignedIdsError('');
    setAssignedIdsLoading(true);

    try {
      const userIds = await featureFlagAdminService.getFeatureFlagUserIds(row.id);

      if (requestSequenceRef.current === requestSequence) {
        setClientConfigurations((currentConfigurations) => ({
          ...currentConfigurations,
          [row.client]: {
            ...currentConfigurations[row.client],
            userIds,
          },
        }));
        setUserAssignmentsChanged(false);
      }
    } catch (error) {
      if (requestSequenceRef.current === requestSequence) {
        const message = getSubmitErrorMessage(error, 'Failed to load assigned users.');
        setAssignedIdsError(message);
        setFeatureFormError(message);
      }
    } finally {
      if (requestSequenceRef.current === requestSequence) {
        setAssignedIdsLoading(false);
      }
    }
  }, [setClientConfigurations, setFeatureFormError, setUserAssignmentsChanged]);

  return {
    assignedIdsError,
    assignedIdsLoading,
    handleLoadAssignedStoreIds,
    handleLoadAssignedUserIds,
    resetAssignedIdsState,
  };
};
